import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Menu } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const ROLE_LABEL = {
  super_admin: "Super Admin",
  institute_admin: "Institute",
  class_teacher: "Class Teacher",
  subject_teacher: "Subject Teacher",
  student: "Student",
};

export const DashboardShell = ({ nav = [], title, children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen bg-secondary/40 flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-background border-r border-border flex flex-col transition-transform ${
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="p-6 border-b border-border">
          <Link to="/dashboard" className="font-heading font-black text-xl tracking-tight text-primary">
            Theta<span className="text-foreground">Exams</span>
          </Link>
          <p className="overline text-muted-foreground mt-2">{ROLE_LABEL[user?.role]}</p>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {nav.map((item) => {
            const active = loc.pathname === item.to;
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.key}`}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                }`}
              >
                {Icon && <Icon className="h-4 w-4" />}
                {item.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-border">
          <div className="mb-3">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-start gap-2"
            data-testid="logout-button"
            onClick={() => {
              logout();
              navigate("/login");
            }}
          >
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </aside>

      {open && <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={() => setOpen(false)} />}

      <main className="flex-1 min-w-0 lg:ml-64">
        <header className="sticky top-0 z-20 bg-white/70 dark:bg-background/70 backdrop-blur-xl border-b border-border px-6 py-4 flex items-center gap-4">
          <button className="lg:hidden" onClick={() => setOpen(true)} data-testid="sidebar-toggle">
            <Menu className="h-5 w-5" />
          </button>
          <h1 className="font-heading font-bold text-xl tracking-tight">{title}</h1>
        </header>
        <div className="p-6 fade-up">{children}</div>
      </main>
    </div>
  );
};

export const StatCard = ({ label, value, sub, icon: Icon, accent = "text-primary", testId }) => (
  <div
    data-testid={testId}
    className="bg-background border border-border rounded-lg p-3 sm:p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-200"
  >
    <div className="flex items-start justify-between">
      <p className="overline text-muted-foreground">{label}</p>
      {Icon && <Icon className={`h-4 w-4 shrink-0 sm:h-5 sm:w-5 ${accent}`} />}
    </div>
    <p className="mt-2 font-heading text-2xl font-black tracking-tight sm:mt-3 sm:text-4xl">{value}</p>
    {sub && <p className="mt-1 text-xs text-muted-foreground sm:text-sm">{sub}</p>}
  </div>
);
